import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
const DASHBOARD_IMG = 'https://cdn.shopify.com/s/files/1/0995/0671/6970/files/2Dashboard_Ratio16_9.jpg?v=1777848099';
const scrollToUpgrades = () => document.querySelector('#upgrades')?.scrollIntoView({
  behavior: 'smooth'
});
const features = ['Complete P&L Dashboard — track revenue, expenses, and real profit', 'Inventory Tracker — never run out of your best sellers again', 'Order & Sales Analytics — see what sells and what doesn\'t', 'Ad Spend ROI Calculator — know exactly what your ads return', 'Monthly Cash Flow Tracker — see where every dollar goes', 'Tax-Ready Reports — save hours at tax time', 'Multi-Platform Support — Shopify, Etsy, Amazon, WooCommerce'];
export function ProductBundle() {
  return <section id="product" className="relative bg-[#060B18] py-24 overflow-hidden" data-id="element-218">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-teal/5 rounded-full blur-[180px] pointer-events-none" data-id="element-219" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-220">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-14" data-id="element-221">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-teal bg-teal/10 px-4 py-1.5 rounded-full mb-5 border border-teal/20" data-id="element-222">
            What's Inside
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-id="element-223">
            One Dashboard.{' '}
            <span className="text-gradient-teal" data-id="element-224">Every Number.</span>
            <br className="hidden sm:block" data-id="element-225" /> Zero Guessing.
          </h2>
          <p className="text-white/50 max-w-xl mx-auto text-base" data-id="element-226">
            A beautifully organized spreadsheet that auto-calculates your profit, tracks inventory in real time, and generates professional invoices.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="glass-card rounded-3xl overflow-hidden" style={{
        boxShadow: '0 0 100px rgba(0,201,177,0.1), 0 40px 80px rgba(0,0,0,0.4)',
        border: '1px solid rgba(0,201,177,0.15)'
      }} data-id="element-227">
          <div className="grid lg:grid-cols-2 gap-0" data-id="element-228">
            {/* Left: features */}
            <div className="p-8 lg:p-12" data-id="element-229">
              <h3 className="font-display font-extrabold text-2xl sm:text-3xl text-white mb-2" data-id="element-230">
                The Founder's Business
                <span className="text-gradient-teal" data-id="element-231"> Command Center</span>
              </h3>
              <p className="text-white/50 text-sm mb-8 max-w-md leading-relaxed" data-id="element-232">
                Google Sheets + Excel bundle. Instant download. Works on any device with no formulas to write and no setup complexity.
              </p>

              <ul className="space-y-3 mb-8" data-id="element-233">
                {features.map(feature => <li key={feature} className="flex items-start gap-3" data-id="element-234">
                    <span className="w-5 h-5 rounded-full bg-teal/15 border border-teal/30 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-235">
                      <CheckIcon className="w-3 h-3 text-teal" data-id="element-236" />
                    </span>
                    <span className="text-white/70 text-sm leading-snug" data-id="element-237">{feature}</span>
                  </li>)}
              </ul>

              {/* Dashboard image preview */}
              <div className="relative rounded-xl overflow-hidden border border-white/10 group" data-id="element-238">
                <img src={DASHBOARD_IMG} alt="Business Command Center Dashboard Preview" className="w-full object-cover transition-transform duration-500 group-hover:scale-105" style={{
                aspectRatio: '16/9'
              }} loading="lazy" data-id="element-239" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-240" />
                <div className="absolute bottom-3 left-3 flex items-center gap-2" data-id="element-241">
                  <span className="w-2 h-2 rounded-full bg-teal animate-pulse" data-id="element-242" />
                  <span className="text-white/70 text-xs font-medium" data-id="element-243">Live dashboard preview</span>
                </div>
              </div>
            </div>

            {/* Right: value summary + CTA */}
            <div className="bg-white/[0.03] border-t lg:border-t-0 lg:border-l border-white/5 p-8 lg:p-12 flex flex-col justify-center" data-id="element-244">
              <div className="text-center" data-id="element-245">
                {/* What's always included */}
                <div className="space-y-2 mb-8 text-left" data-id="element-246">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/25 mb-4" data-id="element-247">
                    Included in all packages
                  </p>
                  {[{
                  name: 'Command Center Dashboard',
                  value: '$59.99 value'
                }, {
                  name: 'Pricing Profit Calculator',
                  value: 'FREE',
                  green: true
                }, {
                  name: '60-Day Launch Checklist',
                  value: 'FREE',
                  green: true
                }].map(item => <div key={item.name} className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2.5" data-id="element-248">
                      <div className="flex items-center gap-2" data-id="element-249">
                        <CheckIcon className="w-3.5 h-3.5 text-teal flex-shrink-0" data-id="element-250" />
                        <span className="text-sm text-white/65" data-id="element-251">{item.name}</span>
                      </div>
                      <span className={`text-xs font-semibold ${item.green ? 'text-teal' : 'text-white/35'}`} data-id="element-252">
                        {item.value}
                      </span>
                    </div>)}
                </div>

                {/* Price display — From $59.99 with $198 total value crossed out */}
                <div className="mb-8" data-id="element-253">
                  <div className="flex items-center justify-center gap-3 mb-1" data-id="element-254">
                    <span className="text-white/30 text-xl line-through" data-id="element-255">$198</span>
                    <div data-id="element-256">
                      <span className="text-white/50 text-lg font-medium" data-id="element-257">From </span>
                      <span className="font-display font-extrabold text-5xl text-gradient-gold" data-id="element-258">$59.99</span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <motion.button whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.97
              }} onClick={scrollToUpgrades} className="w-full bg-gradient-gold text-white font-bold text-base py-4 rounded-full hover:shadow-2xl hover:shadow-[#FF6B00]/30 transition-all duration-300" data-id="element-259">
                  See All Packages & Prices ↓
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}